import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { PaymentMethod } from '../types';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';

interface PaymentMethodsProps {
  paymentMethods: PaymentMethod[];
  onAddMethod: () => void | Promise<void>;
  onEditMethod: (id: string) => void;
  onRemoveMethod: (id: string) => void;
  onSetDefault: (id: string) => void;
  onAttachPaymentMethod?: (paymentMethodId: string) => Promise<void>;
}

export const PaymentMethods: React.FC<PaymentMethodsProps> = ({
  paymentMethods,
  onAddMethod,
  onRemoveMethod,
  onSetDefault,
  onAttachPaymentMethod,
}) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const capitalizeBrand = (brand?: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  const formatExpiryDate = (expMonth?: number, expYear?: number) => {
    if (expMonth && expYear) {
      return `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`;
    }
    return null;
  };

  const handleAddMethod = () => {
    setIsAddModalOpen(true);
  };

  const handleModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleModalSuccess = () => {
    setIsAddModalOpen(false);
    onAddMethod();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('billing.paymentMethods')}
        </h2>
        <Button
          variant="gradient-multi"
          onClick={handleAddMethod}
          className="flex items-center gap-2"
        >
          <span className="material-icons">add</span>
          {t('billing.addNewMethod')}
        </Button>
      </div>
      <Card>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-icons text-4xl text-gray-400 mb-2">credit_card</span>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('billing.noPaymentMethodsSaved')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('billing.paymentMethodsAutoSaved')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paymentMethods
                .filter(method => method.is_active)
                .map(method => {
                  const expiryDate = formatExpiryDate(method.exp_month, method.exp_year);
                  const activeMethodsCount = paymentMethods.filter(m => m.is_active).length;
                  const isLastPaymentMethod = activeMethodsCount <= 1;

                  return (
                    <div
                      key={method.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="material-icons text-primary-500">credit_card</span>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {capitalizeBrand(method.brand || method.card_brand)}{' '}
                              {t('billing.cardEndingIn')}{' '}
                              {method.last4 || method.card_last4 || '****'}
                            </div>
                            {expiryDate && (
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                Expires {expiryDate}
                              </div>
                            )}
                          </div>
                        </div>
                        {method.is_default && (
                          <Badge variant="success" size="sm">
                            {t('billing.default')}
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {!method.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSetDefault(method.id)}
                            className="flex items-center gap-1"
                          >
                            <span className="material-icons text-sm">star</span>
                            {t('billing.setAsDefault')}
                          </Button>
                        )}
                        <div className="relative flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveMethod(method.id)}
                            disabled={isLastPaymentMethod || method.is_default}
                            className={`flex items-center gap-1 ${
                              isLastPaymentMethod || method.is_default
                                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : 'text-red-600 hover:text-red-700'
                            }`}
                            title={
                              isLastPaymentMethod
                                ? t('billing.cannotRemoveLastPaymentMethodDescription')
                                : method.is_default
                                  ? t('billing.cannotRemoveDefaultPaymentMethod')
                                  : t('billing.removePaymentMethod')
                            }
                          >
                            <span className="material-icons text-sm">delete</span>
                            {t('common.remove')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      <AddPaymentMethodModal
        isOpen={isAddModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        onAttachPaymentMethod={onAttachPaymentMethod}
      />
    </div>
  );
};
